import { ClientSelector, init, StorageAdapterLS } from "@opendash/core";
import { getCurrentLanguageSync } from "@opendash/i18n";
import { registerIconPack } from "@opendash/icons";
import { $parse, ParsePlugin } from "@opendash/plugin-parse";
import { _User } from "@opendash/plugin-parse-schema";
import "@xyflow/react/dist/style.css";
import dayjs from "dayjs";
import { GlobalStyles } from "./GlobalStyles";

import {
  MetaConfigButton,
  MetaEntryButton,
  MetaPlugin,
} from "@opendash/plugin-meta";
import { SchedulerMonitoringPlugin } from "@opendash/plugin-scheduler-monitoring";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Parse from "parse";
import { UserRoleDrawer } from "./components/UserRoleDrawer";
import { NavigateToOrderButton } from "./features/orders";
import { OrderDetailPage } from "./pages/OrderDetailPage";
import { OrderOverviewPage } from "./pages/OrderOverviewPage";
import {
  OD3_Tenant,
  SIV_DeliveryNotes,
  SIV_GoodsReceiptProcess,
  SIV_GoodsReceiptProcessPositionText,
  SIV_GoodsReceiving,
  SIV_Order,
  SIV_Package,
  SIV_PackageText,
} from "./parsegenerated";

init("opendash", async (factory) => {
  const clientselector = ClientSelector.getInstance();

  Parse.initialize(clientselector.getParseAppId(), "");
  Parse.serverURL = clientselector.getParseHost();

  registerIconPack(await import("@opendash/icons/dist/fa-regular.json"));
  registerIconPack(await import("@opendash/icons/dist/fa-solid.json"));
  registerIconPack(await import("@opendash/icons/dist/fa-light.json"));

  factory.ui.setLogoImage(clientselector.getLogoImage());

  factory.registerGlobalComponent(GlobalStyles, {});

  // Translations
  factory.registerLanguage("en", "English");
  factory.registerLanguage("de", "Deutsch", "en", true);

  factory.registerAntDesignTranslation(
    "en",
    () => import("antd/lib/locale/en_US"),
  );

  factory.registerAntDesignTranslation(
    "de",
    () => import("antd/lib/locale/de_DE"),
  );

  factory.registerTranslationResolver(
    "de",
    "siv",
    async () => await import("./i18n/de.json"),
  );

  factory.registerTranslationResolver(
    "en",
    "siv",
    async () => await import("./i18n/en.json"),
  );

  // Adapter + Plugins
  factory.registerDeviceStorageAdapter(new StorageAdapterLS());

  await factory.use(
    new ParsePlugin({
      authLdapActive: false,
      useLiveQueries: false,
    }),
  );

  await factory.use(new MetaPlugin());

  await factory.use(
    new SchedulerMonitoringPlugin({
      monitoringUrl: "https://siv.mine.nu/scheduler/api/v1",
    }),
  );

  // Class Config

  $parse.ui.setDefaultView("_User", {
    type: "table",
    showSearch: true,
    additionalActions: [
      <MetaConfigButton
        buttonProps={{ size: "middle" }}
        context={["siv", "user"]}
      />,
    ],
    additionalEntryActions: (object) => {
      if (!object.id) return [];
      return [
        <MetaEntryButton
          openButtonProps={{ type: "text", size: "middle" }}
          context={["siv", "user"]}
          entryProps={{ className: "_User", objectId: object.id }}
        />,
        <UserRoleDrawer user={object as _User} />,
      ];
    },
  });

  $parse.ui.setDefaultView(OD3_Tenant.className, {
    type: "table",
    showSearch: true,
    additionalActions: [
      <MetaConfigButton
        buttonProps={{ size: "middle" }}
        context={["siv", "customer"]}
      />,
    ],
    additionalEntryActions: (object) => {
      if (!object.id) return [];
      return [
        <MetaEntryButton
          openButtonProps={{ type: "text", size: "middle" }}
          context={["siv", "customer"]}
          entryProps={{ className: OD3_Tenant.className, objectId: object.id }}
        />,
      ];
    },
  });

  $parse.ui.setClassConfig<OD3_Tenant>({
    className: OD3_Tenant.className,
    translationPrefix: "siv:classes.",
    titleFields: ["label"],
    displayFields: ["label"],
    createFields: ["label"],
    editFields: ["label"],
  });

  $parse.ui.setDefaultView(SIV_Order.className, {
    type: "table",
    showSearch: true,
    additionalEntryActions: (object) => [
      <NavigateToOrderButton order={object as SIV_Order} />,
    ],
  });

  $parse.ui.setClassConfig<SIV_Order>({
    className: SIV_Order.className,
    translationPrefix: "siv:classes.",
    titleFields: ["no"],
    displayFields: [
      "no",
      "startedAt",
      "appointedAt",
      "finishedAt",
      "responsible",
      "processor",
      "type",
      "commission",
      "cost_center",
      "country",
      "email",
      "description",
      "tenant",
    ],
    createFields: [],
    editFields: [],
  });

  $parse.ui.setDefaultView(SIV_GoodsReceiptProcess.className, {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setClassConfig<SIV_GoodsReceiptProcess>({
    className: SIV_GoodsReceiptProcess.className,
    translationPrefix: "siv:classes.",
    titleFields: ["no"],
    displayFields: [
      "no",
      "date",
      "order",
      "deliveryNote",
      "storageLocation",
      "weight",
      "tenant",
      "comment",
    ],
    createFields: [],
    editFields: [],
  });

  $parse.ui.setDefaultView(SIV_GoodsReceiving.className, {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setClassConfig<SIV_GoodsReceiving>({
    className: SIV_GoodsReceiving.className,
    translationPrefix: "siv:classes.",
    titleFields: ["no"],
    displayFields: [
      "no",
      "date",
      "order",
      "deliveryNote",
      "goodsReceiptProcess",
      "storageLocation",
      "amount",
      "project",
      "description",
      "tenant",
      "comment",
    ],
    createFields: [],
    editFields: [],
  });

  $parse.ui.setDefaultView(SIV_DeliveryNotes.className, {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setClassConfig<SIV_DeliveryNotes>({
    className: SIV_DeliveryNotes.className,
    translationPrefix: "siv:classes.",
    titleFields: ["no"],
    displayFields: [
      "no",
      "date",
      "deliveryAddresses",
      "deliveryAttributes",
      "deliveryDate",
      "order",
      "facility",
      "quantity",
      "transportedBy",
      "transportType",
      "licensePlate",
      "kcNumber",
      "lotNo",
      "remarks",
      "tenant",
      "xcellizeNo",
    ],
    createFields: [],
    editFields: [],
  });

  $parse.ui.setDefaultView(SIV_Package.className, {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setClassConfig<SIV_Package>({
    className: SIV_Package.className,
    translationPrefix: "siv:classes.",
    titleFields: ["no"],
    displayFields: [
      "no",
      "order",
      "boxNumber",
      "assignedGrossWeight",
      "constructionType",
      "customerCategory",
      "customerPackageNo",
      "deliveryDate",
      "deliveryNote",
      "grossWeight",
      "lineNo",
      "netWeight",
      "packageHeight",
      "packageLength",
      "packageNo",
      "packageWidth",
      "packagingCategory",
      "packagingDate",
      "packagingEndDate",
      "packagingLocation",
      "packagingType",
      "packedBy",
      "productionEndDate",
      "productionFacility",
      "shortDescription",
      "sivFacility",
      "statusCompletion",
      "statusInvoice",
      "statusProcess",
      "storageInstruction",
      "surface",
      "tenant",
      "textLine",
      "textLines",
      "transportCode",
      "volume",
      "xcellize",
    ],
    createFields: [],
    editFields: [],
  });

  $parse.ui.setDefaultView(SIV_PackageText.className, {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setClassConfig<SIV_PackageText>({
    className: SIV_PackageText.className,
    translationPrefix: "siv:classes.",
    titleFields: ["no"],
    displayFields: ["no", "package", "tenant", "text"],
    createFields: [],
    editFields: [],
  });

  $parse.ui.setDefaultView(SIV_GoodsReceiptProcessPositionText.className, {
    type: "table",
    showSearch: true,
  });

  $parse.ui.setClassConfig<SIV_GoodsReceiptProcessPositionText>({
    className: SIV_GoodsReceiptProcessPositionText.className,
    translationPrefix: "siv:classes.",
    titleFields: ["no"],
    displayFields: [
      "no",
      "goodsReceiptProcessPosition",
      "tenant",
      "texts",
      "textsF",
    ],
    createFields: [],
    editFields: [],
  });

  // Navigation
  factory.registerRoute({
    path: "/orders",
    permission: [],
    component: async () => ({ default: OrderOverviewPage }),
  });
  factory.registerRoute({
    path: "/orders/details/:id",
    permission: [],
    component: async () => ({ default: OrderDetailPage }),
  });

  factory.registerStaticNavigationGroup({
    id: "siv-admin-group",
    label: "SIV-Auftragsabwicklung",
    order: 100,
    icon: "fa-solid:building-shield",
  });

  factory.registerStaticNavigationItem({
    id: "siv-admin-customers",
    group: "siv-admin-group",
    order: 1,
    label: "Kunden",
    icon: "fa:users",
    place: "sidebar",
    activeCondition: "/admin/parse/OD3_Tenant",
    routeCondition: "/admin/**",
    link: "/admin/parse/OD3_Tenant",
    color: "#0081C7",
    permission: ["siv:can-see-ordermanagement"],
  });

  factory.registerStaticNavigationItem({
    id: "siv-admin-orders",
    group: "siv-admin-group",
    order: 2,
    label: "Aufträge",
    icon: "fa:clipboard",
    place: "sidebar",
    activeCondition: "/admin/parse/SIV_Order",
    routeCondition: "/admin/**",
    link: "/admin/parse/SIV_Order",
    color: "#0081C7",
    permission: ["siv:can-see-ordermanagement"],
  });

  factory.registerStaticNavigationItem({
    id: "siv-admin-receipts",
    group: "siv-admin-group",
    order: 3,
    label: "Wareneingänge",
    icon: "fa:warehouse-alt",
    place: "sidebar",
    activeCondition: "/admin/parse/SIV_GoodsReceiptProcess",
    routeCondition: "/admin/**",
    link: "/admin/parse/SIV_GoodsReceiptProcess",
    color: "#0081C7",
    permission: ["siv:can-see-ordermanagement"],
  });

  factory.registerStaticNavigationItem({
    id: "siv-admin-receivings",
    group: "siv-admin-group",
    order: 4,
    label: "Warenannahmen",
    icon: "fa:arrow-to-right",
    place: "sidebar",
    activeCondition: "/admin/parse/SIV_GoodsReceiving",
    routeCondition: "/admin/**",
    link: "/admin/parse/SIV_GoodsReceiving",
    color: "#0081C7",
    permission: ["siv:can-see-ordermanagement"],
  });

  factory.registerStaticNavigationItem({
    id: "siv-admin-deliverynotes",
    group: "siv-admin-group",
    order: 5,
    label: "Lieferscheine",
    icon: "fa:file-contract",
    place: "sidebar",
    activeCondition: "/admin/parse/SIV_DeliveryNotes",
    routeCondition: "/admin/**",
    link: "/admin/parse/SIV_DeliveryNotes",
    color: "#0081C7",
    permission: ["siv:can-see-ordermanagement"],
  });

  factory.registerStaticNavigationItem({
    id: "siv-admin-packages",
    group: "siv-admin-group",
    order: 6,
    label: "Kisten",
    icon: "fa:box",
    place: "sidebar",
    activeCondition: "/admin/parse/SIV_Package",
    routeCondition: "/admin/**",
    link: "/admin/parse/SIV_Package",
    color: "#0081C7",
    permission: ["siv:can-see-ordermanagement"],
  });

  factory.registerStaticNavigationGroup({
    id: "siv-group",
    label: "SIV",
    order: 100,
    icon: "fa-solid:building-shield",
  });

  factory.registerStaticNavigationItem({
    id: "siv-dashboard",
    group: "siv-group",
    label: "Aufträge",
    order: 1,
    icon: "fa:clipboard",
    activeCondition: "",
    place: "frontpage",
    routeCondition: "/",
    permission: [],
    link: "/orders",
    color: "#0081C7",
  });
}).then(() => {
  console.log("init open.DASH");
  dayjs.locale(getCurrentLanguageSync());
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.tz.setDefault("Europe/Berlin");
});
